<template>
  <div class="content ds-otp" :style="{ '--focus-border-color': props.color }">
    <!-- 隐藏的 password 输入框：供浏览器/扩展识别并自动填充，填充后同步到下方 OTP 六格 -->
    <input
      ref="hiddenPasswordRef"
      type="password"
      autocomplete="current-password"
      name="current-password"
      class="otp-hidden-password"
      maxlength="6"
      inputmode="numeric"
      tabindex="-1"
      aria-label="授权密码"
      @input="onHiddenPasswordInput"
    />
    <!-- 密码提示与明文切换 -->
    <div class="password-tip">
      <p><i>*</i>请输入 6 位数字授权密码：</p>
      <div class="eye-icon" @click="togglePassword">
        <EyeOutlined v-if="showPassword" />
        <EyeInvisibleOutlined v-else />
      </div>
    </div>

    <!-- 6位数字OTP输入框 -->
    <div class="otp-container">
      <div class="ds-otp-wrapper" :class="{ 'show-password': showPassword }">
        <Vue3OtpInput
          v-model:value="otpValue"
          :num-inputs="6"
          input-classes="otp-input"
          inputmode="numeric"
          input-type="number"
          :should-auto-focus="true"
          :should-focus-order="true"
          :conditionalClass="[
            'otp-input-1',
            'otp-input-2',
            'otp-input-3',
            'otp-input-4',
            'otp-input-5',
            'otp-input-6',
          ]"
          :is-disabled="isOtpDisabled"
          @on-change="handleOtpChange"
          @on-complete="handleOtpComplete"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref } from "vue";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons-vue";
import Vue3OtpInput from "vue3-otp-input";

// 组件属性定义
const props = defineProps({
  color: {
    type: String,
    default: "#4749ad",
  },
});

// 自定义事件
const emit = defineEmits(["update:code"]);

// 响应式数据
const otpValue = ref("");
const isOtpDisabled = ref(false);
const showPassword = ref(false);
const hiddenPasswordRef = ref<HTMLInputElement | null>(null);

// 浏览器/扩展自动填充时写入的是隐藏的 password 框，同步到 OTP 六格
function onHiddenPasswordInput(e: Event) {
  const input = e.target as HTMLInputElement;
  const raw = (input.value || "").replace(/\D/g, "").slice(0, 6);
  if (raw.length === 6) {
    otpValue.value = raw;
    emit("update:code", raw.split(""));
    input.value = ""; // 清空隐藏框，避免重复触发
  }
}

// 切换密码显示/隐藏
const togglePassword = () => {
  showPassword.value = !showPassword.value;
};

// 用户通过 OTP 六格输入完成后，把值写回隐藏的 password 框，便于浏览器识别并提示「是否保存密码」
function syncOtpToHiddenPassword(value: string) {
  const normalized = value.replace(/\D/g, "").slice(0, 6);
  if (normalized.length === 6 && hiddenPasswordRef.value) {
    hiddenPasswordRef.value.value = normalized;
  }
}

// 处理OTP输入变化：不做“过滤/拦截”，交给组件本身保证输入合法性
const handleOtpChange = (value: string | string[]) => {
  const strValue = Array.isArray(value) ? value.join("") : value;
  const normalized = strValue.slice(0, 6);
  const codeArray = normalized.padEnd(6, "").split("").slice(0, 6);
  emit("update:code", codeArray);
  syncOtpToHiddenPassword(normalized);
};

// 处理OTP输入完成：不做“过滤/拦截”，交给组件本身保证输入合法性
const handleOtpComplete = (fullValue: string) => {
  const normalized = String(fullValue || "").slice(0, 6);
  const codeArray = normalized.padEnd(6, "").split("").slice(0, 6);
  emit("update:code", codeArray);
  syncOtpToHiddenPassword(normalized);
};

// NOTE:
// IME / 特殊输入兜底拦截逻辑先不启用，优先依赖组件原生能力（inputType=password/number）。
// 如果后续仍发现可以输入字母/中文，再考虑在 wrapper 上启用捕获阶段拦截。
//
// const DIGIT_RE = /^\d+$/;
// const syncFromString = (raw: string) => { ... };
// const handleBeforeInput = (e: InputEvent) => { ... };
// const handleKeydown = (e: KeyboardEvent) => { ... };
// const handlePaste = (e: ClipboardEvent) => { ... };
// const handleCompositionEnd = () => { ... };
</script>
<style>
.ds-otp {
  margin: 20px 0 20px 0;
}

.ds-otp .otp-container {
  margin: 6px 0 20px 0;
}

/* 响应式 - 大屏第一个输入框左侧间距 */
@media (min-width: 768px) {
  .ds-otp .otp-input-1 {
    margin-left: 16px;
  }
}
/* 第三个输入框右侧额外间距 */
.ds-otp .otp-input-3 {
  margin-right: clamp(20px, 12vw, 35px) !important;
}
/* 输入框聚焦样式 */
.ds-otp .otp-input:focus {
  border-color: var(--focus-border-color, rgb(255, 148, 50));
  outline: none;
}

/* 密码提示文字样式 */
.ds-otp .password-tip {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: clamp(12px, 3vw, 14px);
  color: rgba(0, 0, 0, 0.55);
}

.ds-otp .password-tip i {
  color: red;
  font-style: normal;
}

/* 眼睛图标样式 */
.ds-otp .password-tip .eye-icon {
  color: #c0c2c9;
  cursor: pointer;
  font-size: clamp(14px, 3vw, 16px);
  transition: color 0.2s ease;
}

.ds-otp .password-tip .eye-icon:hover {
  color: #4749ad;
}

/* focus color comes from inline CSS variable */
</style>

<style>
.ds-otp .ds-otp-wrapper .otp-input {
  width: clamp(15px, 4.5vw, 40px);
  height: clamp(15px, 4.5vw, 40px);
  padding: 8px;
  font-size: clamp(16px, 4.5vw, 30px);
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  text-align: center;
  outline: none;
  transition: all 0.3s ease;
  -webkit-appearance: none;
  appearance: none;
  margin-right: 15px;
}

/* 非明文状态下强制掩码 */
.ds-otp .ds-otp-wrapper:not(.show-password) input {
  -webkit-text-security: disc !important;
  font-family: "text-security-disc", monospace !important;
}
.ds-otp .ds-otp-wrapper.show-password input {
  -webkit-text-security: unset !important;
  font-family: unset !important;
}

/* 隐藏的 password 框：仅用于密码管理器识别与自动填充，不参与视觉与 tab 顺序 */
.otp-hidden-password {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  opacity: 0;
  pointer-events: none;
}
</style>
